import { apiClient } from './client'

export type ItemCategory = 'laptop' | 'monitor' | 'phone' | 'peripheral' | 'furniture' | 'other'
export type ItemStatus = 'available' | 'assigned' | 'retired' | 'repair'
export type ItemCondition = 'new' | 'good' | 'worn' | 'damaged'

export interface InventoryItem {
  id: string
  name: string
  category: ItemCategory
  asset_tag?: string | null
  status: ItemStatus
  condition?: ItemCondition | null
  quantity: number
  mac_address?: string | null
  notes?: string | null
  purchase_date?: string | null
  purchase_cost?: string | number | null
  vendor?: string | null
  warranty_expiry?: string | null
  image_url?: string | null
  document_url?: string | null
  org_id: string
  created_by?: string | null
  created_at: string
  updated_at: string
  current_holder_id?: string | null
  current_holder_name?: string | null
}

export interface InventoryAssignment {
  id: string
  item_id: string
  user_id: string
  user_name: string
  assigned_at: string
  returned_at?: string | null
  assigned_by?: string | null
  assigned_by_name?: string | null
  note?: string | null
  is_active: boolean
}

export interface InventoryItemDetail extends InventoryItem {
  history: InventoryAssignment[]
}

export interface InventoryListResponse {
  items: InventoryItem[]
  total: number
  page: number
  per_page: number
}

export interface InventoryItemCreate {
  name: string
  category?: ItemCategory
  asset_tag?: string | null
  condition?: ItemCondition | null
  quantity?: number
  mac_address?: string | null
  notes?: string | null
  purchase_date?: string | null
  purchase_cost?: number | string | null
  vendor?: string | null
  warranty_expiry?: string | null
  image_url?: string | null
  document_url?: string | null
}

export type InventoryItemUpdate = Partial<InventoryItemCreate> & { status?: ItemStatus }

export interface ListInventoryParams {
  page?: number
  per_page?: number
  search?: string
  status?: ItemStatus
  category?: ItemCategory
  assigned_to?: string
  unassigned?: boolean
}

// Public (no auth) asset page a QR label points to.
export interface PublicAsset {
  org_name: string
  org_slug: string
  name: string
  category: string
  asset_tag?: string | null
  status: string
  condition?: string | null
  quantity: number
  current_holder_name?: string | null
  assigned_since?: string | null
  warranty_expiry?: string | null
  image_url?: string | null
}

export async function getPublicAsset(orgSlug: string, assetTag: string): Promise<PublicAsset> {
  const response = await apiClient.get<PublicAsset>(
    `/public/inventory/${encodeURIComponent(orgSlug)}/${encodeURIComponent(assetTag)}`,
  )
  return response.data
}

export async function listInventory(params: ListInventoryParams = {}): Promise<InventoryListResponse> {
  const response = await apiClient.get<InventoryListResponse>('/inventory', { params })
  return response.data
}

export async function listMyInventory(): Promise<InventoryItem[]> {
  const response = await apiClient.get<InventoryItem[]>('/inventory/mine')
  return response.data
}

export async function getItem(itemId: string): Promise<InventoryItemDetail> {
  const response = await apiClient.get<InventoryItemDetail>(`/inventory/${itemId}`)
  return response.data
}

export async function createItem(data: InventoryItemCreate): Promise<InventoryItem> {
  const response = await apiClient.post<InventoryItem>('/inventory', data)
  return response.data
}

export async function updateItem(itemId: string, data: InventoryItemUpdate): Promise<InventoryItem> {
  const response = await apiClient.put<InventoryItem>(`/inventory/${itemId}`, data)
  return response.data
}

export async function retireItem(itemId: string): Promise<void> {
  await apiClient.delete(`/inventory/${itemId}`)
}

export async function deleteItemPermanent(itemId: string): Promise<void> {
  await apiClient.delete(`/inventory/${itemId}/permanent`)
}

// ---- Bulk operations -------------------------------------------------------

export interface BulkChanges {
  status?: ItemStatus | null
  category?: ItemCategory | null
  condition?: ItemCondition | null
  vendor?: string | null
}

export interface BulkResult {
  affected: number
}

export async function bulkUpdate(ids: string[], changes: BulkChanges): Promise<BulkResult> {
  const response = await apiClient.post<BulkResult>('/inventory/bulk/update', { ids, changes })
  return response.data
}

export async function bulkAssign(ids: string[], userId: string): Promise<BulkResult> {
  const response = await apiClient.post<BulkResult>('/inventory/bulk/assign', { ids, user_id: userId })
  return response.data
}

export async function bulkRetire(ids: string[]): Promise<BulkResult> {
  const response = await apiClient.post<BulkResult>('/inventory/bulk/retire', { ids })
  return response.data
}

export async function bulkDelete(ids: string[]): Promise<BulkResult> {
  const response = await apiClient.post<BulkResult>('/inventory/bulk/delete', { ids })
  return response.data
}

export async function assignItem(itemId: string, userId: string, note?: string): Promise<InventoryAssignment> {
  const response = await apiClient.post<InventoryAssignment>(`/inventory/${itemId}/assign`, {
    user_id: userId,
    note: note || null,
  })
  return response.data
}

export async function returnItem(itemId: string): Promise<void> {
  await apiClient.post(`/inventory/${itemId}/return`)
}

export async function getItemHistory(itemId: string): Promise<InventoryAssignment[]> {
  const response = await apiClient.get<InventoryAssignment[]>(`/inventory/${itemId}/history`)
  return response.data
}

// ---- Import / Export -------------------------------------------------------

export type SheetFormat = 'csv' | 'xlsx'

export interface ImportError {
  row: number
  message: string
}

export interface ImportResult {
  created: number
  updated: number
  skipped: number
  errors: ImportError[]
}

/** Trigger a browser download of a Blob. */
function saveBlob(blob: Blob, filename: string) {
  const url = URL.createObjectURL(blob)
  const a = document.createElement('a')
  a.href = url
  a.download = filename
  document.body.appendChild(a)
  a.click()
  a.remove()
  URL.revokeObjectURL(url)
}

export async function exportInventory(
  params: ListInventoryParams = {},
  format: SheetFormat = 'csv',
): Promise<void> {
  const response = await apiClient.get('/inventory/export', {
    params: { ...params, format },
    responseType: 'blob',
  })
  const ext = format === 'xlsx' ? 'xlsx' : 'csv'
  const date = new Date().toISOString().slice(0, 10).replace(/-/g, '')
  saveBlob(response.data as Blob, `inventory-${date}.${ext}`)
}

export async function downloadImportTemplate(format: SheetFormat = 'csv'): Promise<void> {
  const response = await apiClient.get('/inventory/import/template', {
    params: { format },
    responseType: 'blob',
  })
  const ext = format === 'xlsx' ? 'xlsx' : 'csv'
  saveBlob(response.data as Blob, `inventory-import-template.${ext}`)
}

export async function importInventory(file: File): Promise<ImportResult> {
  const formData = new FormData()
  formData.append('file', file)
  const response = await apiClient.post<ImportResult>('/inventory/import', formData, {
    headers: { 'Content-Type': 'multipart/form-data' },
  })
  return response.data
}

// ---- Smart (AI-assisted) import --------------------------------------------

export interface ImportPreviewItem {
  name: string
  category: string
  holder?: string | null
  mac_address?: string | null
}

export interface ImportPreview {
  layout: string
  items: number
  assigned: number
  unmatched: number
  sample: ImportPreviewItem[]
  note?: string | null
}

/** Opaque server-produced mapping — passed back to commit unchanged. */
export type SheetMapping = Record<string, unknown>

export interface ImportAnalyzeResponse {
  source: 'ai' | 'heuristic'
  mapping: SheetMapping
  preview: ImportPreview
}

export interface SmartImportResult {
  created: number
  assigned: number
  unmatched: number
  skipped: number
  errors: ImportError[]
}

export async function analyzeImport(file: File): Promise<ImportAnalyzeResponse> {
  const formData = new FormData()
  formData.append('file', file)
  const response = await apiClient.post<ImportAnalyzeResponse>('/inventory/import/analyze', formData, {
    headers: { 'Content-Type': 'multipart/form-data' },
  })
  return response.data
}

export async function commitImport(file: File, mapping: SheetMapping): Promise<SmartImportResult> {
  const formData = new FormData()
  formData.append('file', file)
  formData.append('mapping', JSON.stringify(mapping))
  const response = await apiClient.post<SmartImportResult>('/inventory/import/commit', formData, {
    headers: { 'Content-Type': 'multipart/form-data' },
  })
  return response.data
}
